import {
  faArrowsRotate,
  faCheckCircle,
  faClock,
  faRightLeft,
  faTimesCircle,
  faTriangleExclamation,
  faUserPen
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { memo, useContext, useEffect, useRef, useState } from 'react';

import PrerequisiteStatuses from '@/components/AppointmentType/components/PrerequisiteStatuses';
import ScheduleExpectationCycle from '@/components/AppointmentType/components/ScheduleExpectationCycle';
import Debug from '@/components/Debug';
import SwitchAppointmentDurationModal from '@/components/SwitchAppointmentDurationModal';
import { getCancelSwitchAvailability } from '@/components/SwitchAppointmentDurationModal/getCancelSwitchAvailability';
import { getSwitchDurationAvailability } from '@/components/SwitchAppointmentDurationModal/getSwitchDurationAvailability';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import ScheduleExpectationStatusContext from '@/contexts/ScheduleExpectationStatusContext';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import {
  invalidateCycleQueries,
  useActiveCustomScheduleExpectations,
  useChronologicalScheduleExpectations
} from '@/hooks/useScheduleExpectations/useScheduleExpectations';
import PersonContext from '@/pages/Person/PersonContext';
import AppointmentsRepository from '@/repositories/AppointmentsRepository';
import { scheduleExpectationRepository } from '@/repositories/ScheduleExpectationRepository';
import type {
  PrerequisiteStatus,
  ScheduleExpectationCycle as ScheduleExpectationCycleType
} from '@/types/scheduleExpectation';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Button,
  Pill,
  Tooltip
} from '@/ui';
import Modal from '@/ui/components/Modal';
import { addDaysToDateString, formatWeekdayCalendarDate } from '@/utils/dates';
import isStandaloneCustomExpectation from '@/utils/isStandaloneCustomExpectation';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import type { Props } from './types';

const shouldHideCycleFromList = (cycle: ScheduleExpectationCycleType) => {
  return cycle.expected_this_cycle === 0 && cycle.appointments_scheduled === 0;
};

const AppointmentType = ({
  appointmentType,
  isActive = false,
  onSeeExpectationDetails,
  phase,
  timeZone,
  uiMode = 'default'
}: Props) => {
  const {
    all_expectations_met,
    appointment_type_duration,
    prerequisite_data,
    schedule_expectation_cycles
  } = appointmentType;

  const { addMessage, host } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const { navigateToAppointment } = useNavigateToPage();
  const queryClient = useQueryClient();
  const { data: activeCustomExpectations } = useActiveCustomScheduleExpectations({
    phaseId: phase.id
  });
  const counterpart = appointmentType.counterpart_appointment_type;
  // Current expectations come straight off appointmentType; only the
  // counterpart's full history needs its own request, to detect a pending
  // future change.
  const { data: counterpartNodes } = useChronologicalScheduleExpectations({
    phaseId: phase.id,
    appointmentTypeId: counterpart?.appointment_type_id ?? '',
    enabled: !!counterpart
  });
  const switchAvailability = getSwitchDurationAvailability({
    currentExpectation: appointmentType.current_expectation,
    counterpart,
    counterpartNodes
  });
  const pendingStartAt =
    switchAvailability.status === 'disabled' ? switchAvailability.pendingStartAt : undefined;

  const originatingExpectation = appointmentType.current_expectation;
  const counterpartExpectation = counterpart?.current_expectation;
  // A switch already in motion can only be auto-cancelled when neither leg
  // end-dated an existing custom expectation -- see getCancelSwitchAvailability.
  // Only fetch the originating type's own history (otherwise redundant, see
  // above) when that might be the case.
  const needsOriginatingNodesForCancel =
    !!pendingStartAt &&
    !(
      originatingExpectation?.custom_schedule_expectation_id &&
      originatingExpectation.end_at === pendingStartAt
    ) &&
    !(
      counterpartExpectation?.custom_schedule_expectation_id &&
      counterpartExpectation.end_at === pendingStartAt
    );
  const { data: originatingNodes } = useChronologicalScheduleExpectations({
    phaseId: phase.id,
    appointmentTypeId: appointmentType.appointment_type_id,
    enabled: needsOriginatingNodesForCancel
  });
  const cancelSwitchAvailability =
    pendingStartAt && (!needsOriginatingNodesForCancel || originatingNodes)
      ? getCancelSwitchAvailability({
          pendingStartAt,
          originatingExpectation,
          originatingNodes,
          counterpartExpectation,
          counterpartNodes
        })
      : null;

  const cancelSwitch = useMutation({
    mutationFn: async (availability: {
      originatingFutureNodeId: string;
      counterpartFutureNodeId: string;
    }) => {
      await Promise.all([
        scheduleExpectationRepository.deleteCustomScheduleExpectation({
          host,
          id: availability.originatingFutureNodeId
        }),
        scheduleExpectationRepository.deleteCustomScheduleExpectation({
          host,
          id: availability.counterpartFutureNodeId
        })
      ]);
    },
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Cancelled the scheduled duration change' });
      await invalidateCycleQueries(queryClient, phase.id);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const [showSwitchDuration, setShowSwitchDuration] = useState(false);
  const upcomingRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { status: processingStatus, appointmentTypeIds: processingTypeIds } = useContext(
    ScheduleExpectationStatusContext
  );
  const isAffectedByProcessing =
    (processingStatus === 'processing' || processingStatus === 'complete') &&
    (processingTypeIds === null || processingTypeIds.includes(appointmentType.appointment_type_id));

  const [ignoreTarget, setIgnoreTarget] = useState<PrerequisiteStatus | null>(null);
  const [showIgnoreConfirm, setShowIgnoreConfirm] = useState(false);
  const [existingExpectationId, setExistingExpectationId] = useState<string | undefined>(undefined);

  const executeIgnore = useMutation({
    mutationFn: async ({
      prereq,
      existingId
    }: {
      prereq: PrerequisiteStatus;
      existingId?: string;
    }) => {
      if (existingId) {
        await scheduleExpectationRepository.deleteCustomScheduleExpectation({
          host,
          id: existingId
        });
      }
      const startNodes = await scheduleExpectationRepository.getCustomExpectationStartNodes({
        host,
        phaseId: phase.id,
        appointmentTypeId: prereq.appointment_type_id
      });
      const startAt = startNodes.min_date;
      if (!startAt) throw new Error('No valid start date available');
      return await scheduleExpectationRepository.createCustomScheduleExpectation({
        host,
        data: {
          appointment_type: prereq.appointment_type_id,
          phase: phase.id,
          cycle_time: '4',
          frequency: '0',
          look_ahead_cycles: '0',
          max_attendances: null,
          note: null,
          start_at: startAt,
          time_unit: 'week'
        }
      });
    },
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Prerequisite ignored successfully' });
      setIgnoreTarget(null);
      setExistingExpectationId(undefined);
      setShowIgnoreConfirm(false);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
      setIgnoreTarget(null);
      setExistingExpectationId(undefined);
      setShowIgnoreConfirm(false);
    }
  });

  const checkIgnore = useMutation({
    mutationFn: async (prereq: PrerequisiteStatus) => {
      const expectations = await scheduleExpectationRepository.listActiveCustomScheduleExpectations(
        {
          host,
          phaseId: phase.id
        }
      );
      const existing = expectations.find(
        (e) => e.appointment_type_id === prereq.appointment_type_id
      );
      return { prereq, existingId: existing?.id };
    },
    onSuccess: ({ prereq, existingId }) => {
      if (existingId) {
        setIgnoreTarget(prereq);
        setExistingExpectationId(existingId);
        setShowIgnoreConfirm(true);
      } else {
        executeIgnore.mutate({ prereq });
      }
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const isIgnoring = checkIgnore.isPending || executeIgnore.isPending;

  const handleConfirmIgnore = () => {
    if (!ignoreTarget) return;
    executeIgnore.mutate({ prereq: ignoreTarget, existingId: existingExpectationId });
  };

  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isActive]);

  const activeClasses = isActive ? 'ring-2 ring-blue-400' : '';

  const isMutedUIMode = uiMode === 'muted';
  const isDefaultUIMode = uiMode === 'default';

  const prerequisiteStatuses = prerequisite_data ?? [];
  const prerequisitesNotMet =
    prerequisiteStatuses.length > 0 &&
    !prerequisiteStatuses.some((p) => p.met_at !== null || p.waived);

  // Only cycles where expected_this_cycle === 0 are safe to bulk-cancel; appointments
  // scheduled against a non-zero expectation may still be legitimately needed.
  const cancellableOverscheduledAppointments = schedule_expectation_cycles
    .filter(
      (cycle) =>
        cycle.appointments_scheduled > cycle.expected_this_cycle && cycle.expected_this_cycle === 0
    )
    .flatMap((cycle) =>
      cycle.appointment_attendances
        ? cycle.appointment_attendances
            .filter((attendance) => !attendance.cancelled)
            .map((attendance) => attendance.appointment_attendance_id)
        : []
    );

  const appointmentsRepository = AppointmentsRepository();

  const cancelAllOverscheduled = useMutation({
    mutationFn: async (appointmentIds: string[]) => {
      return await appointmentsRepository.cancelAppointments(appointmentIds);
    },
    onSuccess: (data) => {
      addMessage({ level: 'success', text: `Cancelled ${data.length} overscheduled appointments` });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const totalOverscheduledCount = schedule_expectation_cycles.reduce((acc, cycle) => {
    if (!cycle.silenced_at && cycle.appointments_scheduled > cycle.expected_this_cycle) {
      return acc + (cycle.appointments_scheduled - cycle.expected_this_cycle);
    }
    return acc;
  }, 0);

  const totalUnderscheduledCount = schedule_expectation_cycles.reduce((acc, cycle) => {
    if (!cycle.silenced_at && cycle.appointments_scheduled < cycle.expected_this_cycle) {
      return acc + (cycle.expected_this_cycle - cycle.appointments_scheduled);
    }
    return acc;
  }, 0);

  const totalSilencedCount = schedule_expectation_cycles.filter(
    (cycle) => cycle.silenced_at !== null
  ).length;

  // The muted view shows the complete historical record of every cycle, so
  // zero/zero cycles must stay visible there even though they're hidden from
  // the default actionable-schedule view.
  const visibleScheduleExpectationCycles = isMutedUIMode
    ? schedule_expectation_cycles
    : schedule_expectation_cycles.filter((cycle) => !shouldHideCycleFromList(cycle));
  // A zero-frequency custom expectation has no visible cycles, so also check the active ones.
  const hasActiveCustomExpectation = !!activeCustomExpectations?.some(
    (expectation) =>
      expectation.appointment_type_id === appointmentType.appointment_type_id &&
      !expectation.sequence
  );
  const hasCustomExpectation =
    hasActiveCustomExpectation ||
    visibleScheduleExpectationCycles.some((cycle) =>
      isStandaloneCustomExpectation(cycle.schedule_expectation)
    );
  // The trigger's aria-label overrides its contents, so the icon's meaning must be in it.
  const triggerLabel = hasCustomExpectation
    ? `${appointmentType.appointment_type_name}, contains a custom expectation`
    : appointmentType.appointment_type_name;
  const mostRecentVisibleScheduleExpectationCycleIndex =
    visibleScheduleExpectationCycles.length - 1;

  const title = appointment_type_duration
    ? `${appointmentType.appointment_type_name} (${appointment_type_duration} min)`
    : appointmentType.appointment_type_name;

  const v2AccordionColor = isMutedUIMode
    ? ('neutral' as const)
    : prerequisitesNotMet
      ? ('warning' as const)
      : all_expectations_met
        ? ('primary' as const)
        : ('destructive' as const);

  return (
    <div ref={containerRef} className={`relative rounded-lg ${activeClasses}`}>
      <AccordionItem color={v2AccordionColor} variant="subtle" defaultOpen={isActive}>
        <div className="relative">
          <AccordionTrigger aria-label={triggerLabel}>
            <div className="flex flex-col items-start gap-1 min-w-0 text-left pr-32">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{title}</span>
                {pendingStartAt && (
                  <Pill size="sm" color="neutral" variant="outline">
                    Ends{' '}
                    {formatWeekdayCalendarDate(
                      addDaysToDateString(pendingStartAt.slice(0, 10), -1)
                    )}
                  </Pill>
                )}
                {hasCustomExpectation && (
                  <Tooltip
                    content="This contains a custom expectation"
                    triggerClassName="inline-flex items-center">
                    <FontAwesomeIcon
                      icon={faUserPen}
                      className="text-xs text-uniform-gray-400 dark:text-uniform-gray-500"
                      aria-label="Custom expectation"
                    />
                  </Tooltip>
                )}
                {isAffectedByProcessing && (
                  <span className="text-sm text-uniform-gray-500 flex items-center gap-1.5">
                    <FontAwesomeIcon
                      icon={faArrowsRotate}
                      className={processingStatus === 'processing' ? 'animate-spin' : ''}
                    />
                    {processingStatus === 'processing' ? 'Processing...' : 'Reprocessing complete'}
                  </span>
                )}
              </div>
              {isDefaultUIMode && (
                <div className="text-sm grid gap-x-1.5 gap-y-0.5 items-center [grid-template-columns:auto_1fr]">
                  <FontAwesomeIcon
                    icon={
                      all_expectations_met && !prerequisitesNotMet ? faCheckCircle : faTimesCircle
                    }
                    className={`justify-self-center ${all_expectations_met && !prerequisitesNotMet ? 'text-green-500' : 'text-red-500'}`}
                  />
                  <span>
                    {prerequisitesNotMet
                      ? 'Prerequisites not met'
                      : all_expectations_met
                        ? 'All expectations met'
                        : 'Schedule Needs Review'}
                  </span>
                  {totalOverscheduledCount > 0 && (
                    <>
                      <Pill size="sm" color="destructive" variant="outline">
                        {totalOverscheduledCount}
                      </Pill>
                      <span>Overscheduled</span>
                    </>
                  )}
                  {totalUnderscheduledCount > 0 && (
                    <>
                      <Pill size="sm" color="destructive" variant="outline">
                        {totalUnderscheduledCount}
                      </Pill>
                      <span>Underscheduled</span>
                    </>
                  )}
                  {totalSilencedCount > 0 && (
                    <>
                      <Pill size="sm" color="neutral" variant="outline">
                        {totalSilencedCount}
                      </Pill>
                      <span>Silenced</span>
                    </>
                  )}
                  {appointmentType.self_schedule_disabled && (
                    <>
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="justify-self-center text-yellow-500 text-xs"
                        aria-label="Self-scheduling disabled for this member"
                      />
                      <span>Self-scheduling disabled</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </AccordionTrigger>

          {!isMutedUIMode && switchAvailability.status !== 'hidden' && counterpart && (
            <div className="absolute right-12 top-3">
              <Tooltip
                content={switchAvailability.status === 'disabled' ? switchAvailability.reason : ''}
                disabled={switchAvailability.status !== 'disabled'}
                placement="bottom">
                <Button
                  variant="outline"
                  color="neutral"
                  size="sm"
                  leftIcon={switchAvailability.status === 'disabled' ? faClock : faRightLeft}
                  disabled={switchAvailability.status === 'disabled'}
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowSwitchDuration(true);
                  }}>
                  Change to {counterpart.appointment_type_duration} min
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
        <AccordionContent className="text-uniform-gray-900 dark:text-uniform-gray-100">
          {pendingStartAt && counterpart && (
            <div className="mb-2">
              <Alert variant="info">
                Switching to <strong>{counterpart.appointment_type_name}</strong> starting{' '}
                {formatWeekdayCalendarDate(pendingStartAt)}.{' '}
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() =>
                    upcomingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }>
                  View upcoming
                </button>
              </Alert>
            </div>
          )}
          {appointmentType.self_schedule_disabled && (
            <div className="relative mb-2">
              <Alert variant={isMutedUIMode ? 'info' : 'warning'}>
                {isMutedUIMode ? (
                  <>
                    Historical mode is enabled. Please turn it off in order to view disabled
                    warnings.
                  </>
                ) : (
                  <div>
                    <p className="font-medium">
                      Self scheduling is disabled for this appointment type:
                    </p>
                    {appointmentType.self_schedule_disabled_reasons.map((reason) => (
                      <p key={reason.code}>{reason.description}</p>
                    ))}
                  </div>
                )}
              </Alert>
            </div>
          )}

          <PrerequisiteStatuses
            prerequisiteStatuses={prerequisiteStatuses}
            onNavigateToAppointment={(appointmentId) =>
              navigateToAppointment(person.id, appointmentId)
            }
            onNavigateToWaivedExpectation={
              onSeeExpectationDetails
                ? (appointmentTypeId, cycleId) =>
                    onSeeExpectationDetails(appointmentTypeId, cycleId)
                : undefined
            }
            onIgnore={!isMutedUIMode ? (prereq) => checkIgnore.mutate(prereq) : undefined}
            isIgnoring={isIgnoring}
          />

          <div>
            <div className="gap-y-4">
              {visibleScheduleExpectationCycles.length > 0 ? (
                visibleScheduleExpectationCycles.map((scheduleExpectationCycle, index) => (
                  <ScheduleExpectationCycle
                    isMostRecent={index === mostRecentVisibleScheduleExpectationCycleIndex}
                    key={scheduleExpectationCycle.cycle_number}
                    memberTimeZone={timeZone}
                    onSeeExpectationDetails={onSeeExpectationDetails}
                    phaseId={phase.id}
                    scheduleExpectationCycle={scheduleExpectationCycle}
                    appointmentType={appointmentType}
                    uiMode={uiMode}
                  />
                ))
              ) : (
                <p className="text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
                  No actionable cycles for this appointment type.
                </p>
              )}
            </div>
          </div>

          {pendingStartAt && counterpart && (
            <div
              ref={upcomingRef}
              className="mt-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
              <div className="p-4 flex flex-wrap justify-between items-center gap-3">
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="font-medium">
                      {counterpart.appointment_type_name} ({counterpart.appointment_type_duration}{' '}
                      min)
                    </span>
                    <Pill size="sm" color="primary" variant="subtle">
                      Upcoming · Starts {formatWeekdayCalendarDate(pendingStartAt)}
                    </Pill>
                  </div>
                  <span className="text-sm text-uniform-gray-600 dark:text-uniform-gray-400">
                    {originatingExpectation?.frequency ?? 1} per{' '}
                    {originatingExpectation?.time_unit ?? 'week'} · replaces{' '}
                    {appointmentType.appointment_type_name}
                  </span>
                </div>
                {!isMutedUIMode && (
                  <Tooltip
                    content={
                      cancelSwitchAvailability?.status === 'disabled'
                        ? cancelSwitchAvailability.reason
                        : ''
                    }
                    disabled={cancelSwitchAvailability?.status !== 'disabled'}
                    placement="top">
                    <Button
                      variant="outline"
                      color="destructive"
                      size="sm"
                      disabled={
                        cancelSwitchAvailability?.status !== 'available' || cancelSwitch.isPending
                      }
                      onClick={() =>
                        cancelSwitchAvailability?.status === 'available' &&
                        cancelSwitch.mutate(cancelSwitchAvailability)
                      }>
                      Cancel change
                    </Button>
                  </Tooltip>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-row gap-3 mt-3">
            {!isMutedUIMode && cancellableOverscheduledAppointments.length > 0 && (
              <Button
                variant="default"
                color="destructive"
                size="sm"
                disabled={cancelAllOverscheduled.isPending || cancelAllOverscheduled.isSuccess}
                onClick={() => cancelAllOverscheduled.mutate(cancellableOverscheduledAppointments)}>
                Cancel all overscheduled ({cancellableOverscheduledAppointments.length})
              </Button>
            )}
          </div>

          <Debug object={appointmentType} />
        </AccordionContent>
      </AccordionItem>

      {counterpart && showSwitchDuration && (
        <SwitchAppointmentDurationModal
          appointmentType={appointmentType}
          onClose={() => setShowSwitchDuration(false)}
          open={showSwitchDuration}
          phase={phase}
          timeZone={timeZone}
        />
      )}

      <Modal
        open={showIgnoreConfirm}
        onClose={() => {
          setShowIgnoreConfirm(false);
          setIgnoreTarget(null);
        }}
        title="Ignore prerequisite"
        textContent={
          ignoreTarget ? (
            <span>
              A custom schedule expectation already exists for{' '}
              <strong>{ignoreTarget.appointment_type_name}</strong>. It will be deleted and replaced
              with a zero-frequency expectation. Do you want to continue?
            </span>
          ) : undefined
        }
        button={{
          children: 'Confirm',
          color: Color.GREEN,
          disabled: isIgnoring,
          loading: isIgnoring,
          onClick: handleConfirmIgnore
        }}
        dismissButton={{
          children: 'Cancel',
          color: Color.GRAY
        }}
      />
    </div>
  );
};
export default memo(AppointmentType);
